import React, { MutableRefObject, ReactNode } from "react";
import {
  ElementSource,
  Media as MediaChq,
  Organization,
  TPromptQuestion,
  Story,
  StoryQuestion as StoryQuestionChq,
  SuggestionContent,
  UnsplashImageResponse,
  User,
  UserActivity
} from "chq";
import { ImageFieldValue } from "@culturehq/components/dist/components/form/ImageField";
import { ElementState } from "@creatomate/preview";
import { SelectedMedia as SelectedMediaChq, VideoDimension } from "../types";

export type SelectedMedia = SelectedMediaChq & {
  elements?: ElementState[];
}

export type Media = MediaChq & {
  id?: number;
  size: string;
};

export type CallableChildren = ((cb: () => void) => ReactNode) | ReactNode;

export type MediaData = ElementSource[];

export type SetState = (newState: Partial<PostStoryState>) => void;

export type DefaultStory = {
  question: StoryQuestion | null;
  body: string | null;
};

export type StoryQuestion = StoryQuestionChq & {
  storyQuestionTags?: any[];
  summary?: string;
};

export type NewPublicUser = {
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl: ImageFieldValue;
  title: string;
  locationName: string[];
  departmentNames: string[];
};

export type HandleSubmit = {
  forceSubmit?: boolean;
  newPublicUser?: Partial<NewPublicUser>;
  questions?: Partial<TPromptQuestion>[];
  suggestions?: SuggestionContent[];
};

export type HandleMediaChange = {
  media: Blob;
  thumb: Blob;
  duration?: number;
  finalVersion?: boolean;
  isRecordedVideo?: boolean;
};

type Notification = {
  id: number;
  notificationType: string;
  entity: Story;
};

export type PostStoryProviderProps = {
  children?: CallableChildren;
  isQuickAction?: boolean;
  isTrendingStory?: boolean;
  isProfile?: boolean;
  currentIndex?: number;
  initialOpen?: boolean;
  openModal?: boolean;
  path?: string;
  preFilledQuestion?: StoryQuestion;
  prompts?: StoryQuestion[];
  queryString?: string[];
  story?: Story;
  selectedStory?: Story;
  showPoints?: boolean;
  startYourTrend?: boolean;
  organization?: Organization;
  onCloseStoryModal?: () => void;
  onDeleteStory?: () => void;
  onPauseVideo?: () => void;
  onPosted?: (story: Story, userActivity: UserActivity) => void;
  onPromptChange?: () => void;
  onStoryClick?: () => void;
  onUpdateStory?: () => void;
  onAnotherModalOpen?: (updating: boolean) => void;
  onNotification?: (notification: Notification) => void;
};

export type StoryWithNullableQuestion = Omit<Story, "question"> & {
  question: StoryQuestion | null;
  externalState?: string;
};

export type PostStoryState = {
  needUrlClean: boolean;
  changed: boolean;
  userSelectedImage: boolean;
  selectedMedia?: SelectedMedia | MediaChq | string | User["avatar"];
  selectedVideos?: File [];
  selectedVideosChanged?: boolean;
  selectedChannel: string;
  showUnsplashSearch: boolean;
  selectedOptions: any[];
  videoDuration?: number;
  thumbnail?: Blob | string;
  selectedThumbnailUrl?: Blob | string;
  progress: number;
  videoDimension: VideoDimension;
  postingStory: boolean;
  selectedQuestion?: StoryQuestion | string | number;
  searchValue?: StoryQuestion | string;
  error?: string | object;
  selectedPrompt?: StoryQuestion;
  userAnswers: number;
  currentStory?: StoryWithNullableQuestion;
  currentBody?: string;
  selectedImages: (
    Media
    | (Media & UnsplashImageResponse)
    | File
    | SelectedMedia
  )[];
  mappersValueIds: string[];
  mediaData: MediaData;
  indexImageToCrop: number;
  cropperOpened?: boolean;
  webVideoRecorded: boolean;
  userInformationRequest: boolean;
  questions: Partial<TPromptQuestion>[];
  suggestions: SuggestionContent[];
  pausedVideo: boolean;
};

export interface PostStoryLocals {
  state: PostStoryState;
  open: boolean;
  canContinueCheck: boolean;
  editorRef: MutableRefObject<null>;
  editorCopyRef: MutableRefObject<null>;
  aiStoryBodyRef: MutableRefObject<null | string>;
  selectedOptionsRef: MutableRefObject<any[]>;
  searchBarRef?: MutableRefObject<any>;
  isCrossTenant: boolean;
  setState: SetState;
  onOpen: () => void;
  onConfirmClose: () => void;
  onConfirmCloseAi: () => void;
  onChange: () => void;
  handleSubmit: (params: HandleSubmit) => void;
  handleOnChangeBody: (value: string) => void;
  handleMediaChange: (props: HandleMediaChange) => Promise<void>;
  onShareWithChanged: (newOptions: any[]) => void;
  handleSelectedChannel: (channel: string) => void;
  handleSelectedJobMappings: (_value: Record<string, string[]>) => void;
  handleSelectImage: (image: SelectedMedia) => void;
  handleFindOnUnsplash: (value: boolean) => void;
  handleSelectQuestion: (question: StoryQuestion | string) => void;
  handleUserAnswers: (userAnswers: number) => void;
  getSelectedMediaUrl: (media: SelectedMedia | null) => string | null;
  handleMediaEdited: (
    index: number,
    value: ElementSource,
    thumbnail: PostStoryState["thumbnail"]
  ) => void;
  handleRemoveImage: (indexToRemove: number) => void;
  handleFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCropperClose: () => void;
  handleOpenCropImage: (imageIndex: number) => void;
  handleVideoEdited: (thumb: any) => void;
  handleEditQuestion: () => void;
  handleUpdateImages: (images: PostStoryState["selectedImages"]) => void;
  cleanedMediaData: PostStoryState["mediaData"];
  restartState: () => void;
  restartSelectedMedia: () => void;
}

export interface UserInformationState {
  email?: string;
  error?: string;
  userAccessStatus?: string;
  agreed: boolean;
  policiesAgreed: boolean[];
  creator?: User;
  organization: Partial<Organization>;
  posting: boolean;
}

export type UserInformationValues = Partial<NewPublicUser>;

export enum AuthorizedFields {
  name = "name",
  avatarUrl = "avatarUrl",
  title = "title",
  locationName = "locationName",
  departmentNames = "departmentNames",
}
